import { useState, useEffect } from "react";
import { Calendar as CalIcon } from "lucide-react";

/**
 * A date field that always shows and accepts DD/MM/YYYY, whatever the browser
 * locale is (a native <input type="date"> renders MM/DD/YYYY on US Chrome,
 * which confuses Indian users). The value in / out is still a plain
 * "YYYY-MM-DD" string, so this is a drop-in replacement for the old native
 * inputs — onChange gets a synthetic { target: { value } } event.
 *
 * A transparent native date input sits over the calendar icon so people who
 * prefer to click still get the OS date picker.
 */

function isoToText(v) {
  if (!v) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v).slice(0, 10));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

function textToIso(t) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(t || "");
  if (!m) return "";
  const d = +m[1], mo = +m[2], y = +m[3];
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 2200) return "";
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

// raw keystrokes -> progressively masked "DD/MM/YYYY"
function mask(v) {
  const digits = v.replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join("/");
}

export default function DateInput({ value, onChange, className, "data-testid": testid, ...rest }) {
  const [text, setText] = useState(isoToText(value));

  // Re-sync when the parent value changes (record loaded, form reset) — but
  // leave a half-typed entry alone if it already maps to that value.
  useEffect(() => {
    const iso = String(value || "").slice(0, 10);
    setText((cur) => (textToIso(cur) === iso ? cur : isoToText(value)));
  }, [value]);

  const emit = (iso) => onChange && onChange({ target: { value: iso } });

  const onText = (e) => {
    const m = mask(e.target.value);
    setText(m);
    emit(m.length === 10 ? textToIso(m) : "");
  };

  const onNative = (e) => {
    const iso = e.target.value; // "YYYY-MM-DD" or ""
    setText(isoToText(iso));
    emit(iso);
  };

  return (
    <div className="relative">
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/YYYY"
        className={`${className || "ep-input"} pr-9`}
        value={text}
        onChange={onText}
        data-testid={testid}
      />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
        <CalIcon size={16} />
      </span>
      <input
        type="date"
        tabIndex={-1}
        aria-label="Pick a date"
        value={textToIso(text)}
        onChange={onNative}
        className="absolute right-0 top-0 h-full w-9 cursor-pointer opacity-0"
      />
    </div>
  );
}
